import React from 'react';
import { useHistory, Prompt } from 'react-router-dom';
import useAuth from '../../hooks/use-auth';
import { toast } from 'react-toastify';
import dayjs from 'dayjs';
import { ROUTES, getSubmitProjectPageTitle } from '../../routing';
import PageWithAsyncData from '../../components/PageWithAsyncData';
import PageWrapper from '../../components/PageWrapper';
import Navbar from '../../components/Navbar';
import { H1, H3, P } from '../../components/elements/Text';
import Button from '../../components/elements/Button';
import { MultiLineInput } from '../../components/elements/Input';
import { Helmet } from 'react-helmet-async';
import * as CrashReporter from '../../crash-reporter';
import { useMutation } from 'react-query';
import { useTeamsProfile } from '../../hooks/use-hydra';
import utc from 'dayjs/plugin/utc';
import Markdown from '../../components/Markdown';
import { postProjectSubmission } from '../../api/teams';

dayjs.extend(utc);
const questions = [
  {
    label: 'Company Project Idea(s)',
    question:
      'Do you already have a sense of what company project you might work on? If so, describe one or more options high-level.',
  },
  {
    label: 'Application',
    question:
      'High-level, what problems will your company project ideas solve? What impact could they have on your role or company?',
  },
  {
    label: 'Time Estimate',
    question:
      'Do you think you can complete the project within 5-10 hours? Are you accounting for any unknown problems?',
  },
  {
    label: 'Next Steps',
    question:
      'What do you need to think about or do before deciding on the specifics of your project?',
  },
  {
    label: 'Extra Help',
    question: 'What else would be helpful from your mentor or colleagues?',
  },
];

const INTRO_TEXT_MARKDOWN = `
**The project is a practical application of your newly acquired skills**.

> 💡 The company project should demonstrate your ability to use your new skills within a current workflow or other use case at work right now.

---

### Choosing your Company Project

Your company or mentor might have already shared a specific company project for you to work on. If so, skim through this first section! These tips and how to approach choosing your project are useful for context either way though.

#### 1️⃣ Enhance Your Workflow

Your company project should ideally help you transform daily or regular tasks by applying your new skills to existing challenges. The most impactful projects address actual pain points in your work environment.
> 💡 Look for processes to improve which **take a lot of time, can be automated or streamlined, or where you are currently bottlenecked by others**.

#### 2️⃣ Leverage Specialized Tools
If your company uses specialized AI or data products, think about how you can incorporate them into your company project. This approach helps you become self-sufficient in using company-specific tools within workflows.

> 🔍 This path often requires permissions to access company-specific systems - reach out to stakeholders early!

#### 3️⃣ Explore Public Resources

Your new skills might open doors to vast public information repositories. This project approach focuses on finding, curating, and analyzing external resources to extract valuable insights. Consider technical aspects like access methods, local environments, and data transformation.

#### 4️⃣ Build a New Workflow

If you're feeling especially ambitious, consider creating a new workflow or system from scratch. But again, make sure you focus on the resulting impact on your role or company as the true north here. This path allows you to also practice “full-stack project execution” skills - design principles, hosting considerations, automation, end-to-end interactions, and of course corralling internal stakeholders where appropriate.

---

### Final Considerations

**Confidentiality**: Project assessment focuses on your process, not sensitive information. You and your organization remain the judges of the results' usefulness.

**Scope Management**: Be realistic about development time! Start small and focused, then expand if time permits.

*Choose a company project that aligns with your workflows as well as available resources and time, to maximize impact.*
`;

export default function CheckIn1() {
  const { getTokenSilently } = useAuth();
  const {
    value: teamsProfile,
    loading: teamsProfileLoading,
    error: teamsProfileError,
    retry: retryTeamsProfile,
  } = useTeamsProfile();
  const history = useHistory();

  const latestCohort =
    teamsProfile?.onboardedCohorts.sort(
      (a, b) =>
        dayjs.utc(b.cohort.startDate).valueOf() -
        dayjs.utc(a.cohort.startDate).valueOf()
    )[0] ?? null;

  const [answers, setAnswers] = React.useState(
    [...Array(questions.length)].map(() => '')
  );
  const areAnswersFilledIn = answers.map((a) => a.length >= 3);

  const onQuestionInput = React.useCallback(
    (value: string, i: number) =>
      setAnswers(answers.map((answer, idx) => (i === idx ? value : answer))),
    [answers]
  );

  React.useEffect(() => {
    if (!latestCohort && !teamsProfileLoading) {
      toast.error(
        'You are not part of any cohort. Please reach out to us to resolve the issue.'
      );
      history.push(ROUTES.HOME.getPath());
    } else {
      // Prefill answers from latest CHECKIN_1 submission
      const submission = latestCohort?.milestones?.PROJECT_CHECKIN_1;
      if (submission && submission.survey) {
        const prefilled = questions.map(
          (_, i) => submission.survey[i]?.answer || ''
        );
        setAnswers(prefilled);
      }
    }
  }, [latestCohort, history, teamsProfileLoading]);

  const isSubmitted =
    !!latestCohort?.milestones?.PROJECT_CHECKIN_1?.submittedAt;

  const submitAnswers = useMutation({
    mutationFn: async () => {
      const token = await getTokenSilently();
      const formData = new FormData();
      formData.append(
        'survey',
        JSON.stringify(
          answers.map((answer, i) => ({
            answer,
            question: questions[i].question,
          }))
        )
      );
      formData.append('submissionType', 'CHECKIN_1');
      await postProjectSubmission(token, {
        formData,
        cohortId: latestCohort?.cohort.id.toString() ?? '',
        learnerId: teamsProfile?.learnerId?.toString() ?? '',
      });
    },
    onSuccess: async () => {
      await retryTeamsProfile();
      toast.success('Check-in #1 submitted successfully!');
      history.push(ROUTES.HOME.getPath());
    },
    onError: (err) => {
      CrashReporter.reportError(err as unknown as Error);
      toast.error(
        'Something went wrong. Please reach out to us to resolve the issue.'
      );
    },
  });

  const hasUnsavedChanges = React.useMemo(() => {
    if (submitAnswers.isLoading) return false;
    const submission = latestCohort?.milestones?.PROJECT_CHECKIN_1;

    const originalAnswers = submission?.survey?.length
      ? questions.map((_, i) => submission.survey[i]?.answer || '')
      : [...Array(questions.length)].map(() => '');

    if (!submission) {
      return false;
    }
    return answers.some((a, i) => a !== originalAnswers[i]);
  }, [answers, latestCohort, submitAnswers.isLoading]);

  // beforeunload handler
  React.useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (hasUnsavedChanges) {
        e.preventDefault();
        e.returnValue = '';
      }
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [hasUnsavedChanges]);

  return (
    <PageWithAsyncData
      className={''}
      isLoading={teamsProfileLoading}
      error={teamsProfileError}
      retry={retryTeamsProfile}
    >
      <>
        <Helmet>
          <title>{getSubmitProjectPageTitle()}</title>
        </Helmet>
        <Navbar
          breadCrumbs={[
            {
              label: 'Home',
              url: ROUTES.HOME.getPath(),
            },
            {
              label: 'Company Project Check-in #1',
              url: null,
            },
          ]}
        />
        <PageWrapper
          style={{
            margin: '0 auto 52px',
            maxWidth: 820,
          }}
        >
          <H1>Project Check-in #1</H1>
          <Markdown md={INTRO_TEXT_MARKDOWN} />
          {questions.map(({ label, question }, i) => (
            <>
              <H3>{label}</H3>
              <P>{question}</P>
              <MultiLineInput
                minRows={3}
                placeholder={'Answer here...'}
                value={answers[i]}
                onChange={(e) => onQuestionInput(e.target.value, i)}
              />
            </>
          ))}
          <div style={{ marginTop: '52px' }}>
            <Button
              onClick={submitAnswers.mutate}
              isLoading={submitAnswers.isLoading}
              isDisabled={!areAnswersFilledIn.every(Boolean)}
              label={
                isSubmitted ? 'Update Check-in #1' : 'Complete Check-in #1'
              }
              type="primary"
            />
          </div>
          <Prompt
            when={hasUnsavedChanges}
            message="You have unsaved changes. Are you sure you want to leave this page?"
          />
        </PageWrapper>
      </>
    </PageWithAsyncData>
  );
}
